// THE FORMATTER SEAM. Every date, clock, age and dollar amount the console
// prints comes from here, so one screen cannot mix locale policies.
//
// It did. `lib/time.ts` pinned `'en'`, `MemberRow` passed `undefined` (the
// BROWSER's locale), ten call sites called bare `toLocaleDateString()` (also the
// browser's), and eleven more pinned `'en-US'` — so a Spanish-browser user on
// `/org/x/account/members` read joined dates in Spanish and, one click later,
// the audit tab's dates in English. Same column type, three conventions, none of
// them the one the COPY beside them was translated into.
//
// The locale is `t.locale` — the translator's own, which exists for exactly this
// ("a translator IS a catalog bound to a locale", `packages/i18n/src/translator.ts`).
// Copy and numbers now answer to one setting instead of two.
//
// `since()` is the exception, and deliberately: `12s` / `3h` / `2d` is a DENSE
// CONTROL-PLANE CELL, not a sentence — the width is the point, and `Intl`'s
// narrow style still translates the unit. It stays ASCII until someone keys it.
//
// Dependency-free beyond Intl; every entry point is NaN-safe and passes an
// unparseable input through verbatim (#357).

import { createSignal, onCleanup } from 'solid-js';
import { t } from '../i18n';

/**
 * Built once per module load, off the translator resolved at boot (the stored
 * `dz-locale` choice, else `resolveBrowserLocale`). The SPA has no per-request locale to plumb and the
 * translator itself is a module singleton, so a formatter rebuilt per call would
 * pay `Intl`'s construction cost on every row of every list to produce the same
 * output. A locale change is a full-page navigation, which discards both — the
 * top bar's language picker (#6222) saves its choice and reloads for exactly
 * this reason.
 *
 * TRIP CONDITION — READ THIS IF YOU ARE ADDING AN IN-APP LANGUAGE SWITCHER.
 * The line above is an ASSUMPTION, not a property: every formatter below is
 * frozen at the boot locale. A switcher that rebinds the translator in place
 * would leave all of them on the old locale while the copy around them changed
 * — the "same column type, two conventions" bug this module was rewritten to
 * kill, reintroduced from the other end and invisible to every test in the
 * suite, because each formatter would still be internally consistent.
 *
 * So the assumption is GUARDED, not merely written down:
 * `testing/formatter-seam.test.ts` asserts that `translatorForLocale` has
 * exactly one non-test caller (`src/i18n.ts`) and that `t` is a `const`. A
 * switcher cannot be built without breaking one of those, and that test will
 * send you back here. When it does, the fix is to make these accessors reading
 * a reactive locale — not to widen the guard.
 */
const LOCALE = t.locale;

const WHEN = new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium', timeStyle: 'short' });
const REL = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });
const MONEY = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'USD' });

// The same stamp as `WHEN` with the viewer's zone abbreviation appended ("Oct 2,
// 2026, 4:10 PM GMT+8"). Built on `LOCALE` like every formatter here — NEVER
// `Intl.DateTimeFormat(undefined, …)`, which reads the BROWSER's locale and
// reintroduces the copy-in-one-language, dates-in-another defect the header above
// records. `lib/time.ts` is exempt from `testing/formatter-seam.test.ts`'s count
// guard, so this comment is the only thing standing between this line and a reversal.
//
// Spelled out as components, not `dateStyle: 'medium'` + `timeStyle: 'short'`: the
// runtime throws a TypeError at module load when `timeZoneName` rides along with
// either style option ("dateStyle and timeStyle may not be used with other
// DateTimeFormat options"), which would blank the whole dashboard, not one hover.
const WHEN_ZONE = new Intl.DateTimeFormat(LOCALE, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});

/** NaN-safe absolute timestamp for `title` / `datetime` attributes. */
export function absolute(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : WHEN.format(d);
}

/**
 * `absolute`, plus the zone the clock is read in: for a hover that states WHEN a
 * thing happens in the viewer's own time ("Oct 2, 2026, 4:10 PM GMT+8"), where a
 * bare local clock would leave an operator in another zone guessing which one.
 */
export function absoluteWithZone(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : WHEN_ZONE.format(d);
}

const MINUTE_MS = 60_000;

/**
 * A countdown to `iso`, compact like `since` ("4h 10m", "25m", "1d 3h") and
 * measured from `now` (a parameter, so a caller driving a ticking clock signal
 * renders from ONE reading of the time and a test can pin it).
 *
 * Rounds a partial minute UP: a countdown that said "4h 9m" with 4h 9m 30s left
 * would undersell the wait. Anything under a minute, or already past, is "under a
 * minute" — a negative duration has no honest reading, and a deadline that just
 * passed IS about to be over. Whole units drop their zero part ("4h", "1d"). Like
 * `since`, the unit letters stay ASCII; only the sentence-shaped fallback is copy.
 *
 * Unparseable input passes through verbatim (#357).
 */
export function untilLabel(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const ms = then - now;
  if (ms < MINUTE_MS) return t('agents.pool.under_a_minute');
  const totalMins = Math.ceil(ms / MINUTE_MS);
  const days = Math.floor(totalMins / 1440);
  const hours = Math.floor((totalMins % 1440) / 60);
  const mins = totalMins % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  return `${mins}m`;
}

/**
 * Compact age for dense control-plane cells: "12s", "2m", "3h", "2d". `now` is a
 * parameter (like `untilLabel`'s) so a row that re-reads a ticking clock signal
 * renders from ONE reading of the time, and a test can pin it.
 */
export function since(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const secs = Math.max(0, Math.round((now - then) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}

// `en-GB` on purpose and NOT `LOCALE`: this is a 24-hour wall-clock stamp for
// live feeds, where every row must be the same width and comparable at a glance.
// A locale that resolves to 12-hour puts an am/pm on half the rows and pushes
// the column. The format is a layout decision, not a language one.
const CLOCK = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/**
 * Whole minutes from `iso` to `now`, floored at 0, or `null` for an unparseable input:
 * Fleet Live's "N min in flight" (#6778). A NUMBER, not a label, because the copy around
 * it is a translated sentence (`fleet.live.tip_where`), and the deck re-reads it every
 * paint off one `now`.
 */
export function minutesSince(iso: string, now: number = Date.now()): number | null {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now - then) / MINUTE_MS));
}

/** "14:03:07" wall-clock stamp for live feeds. */
export function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : CLOCK.format(d);
}

// `en-GB` on purpose, matching `CLOCK`: the day-prefix stamp appears in the
// SAME column as the bare clock (home-audit, home-feed), so the locale choice
// has to agree with `CLOCK`'s — the day-month order and the lack of am/pm are
// what keep the column width predictable when same-day and older-day rows sit
// next to each other. Built once at module load; the seam is frozen at the
// boot locale (see the TRIP CONDITION above).
const DAY_STAMP = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
});

/**
 * A clock stamp that falls back to a date+time for older rows. The home-feed
 * and home-audit columns live on the same wall-clock grid (`clock(iso)`,
 * `HH:MM:SS`) — until a row is from a previous calendar day, where the bare
 * clock loses the only fact that distinguishes it: the date. The bare `HH:MM:SS`
 * shape tells the operator nothing about WHEN that older event happened; a
 * `19 Sept, 19:41:46` prefix does, and the column stays a single grid because
 * the time portion is the same wall-clock stamp `clock` already prints.
 *
 * "Same calendar day" is the LOCAL day — the column is rendered in the
 * operator's browser, not a UTC console, and "today" means "today here".
 *
 * Unparseable input passes through verbatim (#357): the value that arrived is
 * what the next debugger needs, not the unset marker.
 */
export function clockOrDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return CLOCK.format(d);
  return `${DAY_STAMP.format(d)}, ${CLOCK.format(d)}`;
}

// A wall-clock instant pinned to UTC — for a deadline the SERVER states in UTC
// (a provider's quota reset), where the operator compares it against a status
// page or a log line, not their own timezone. 24-hour `en-GB` for the reason
// `CLOCK` gives; the date half is `LOCALE`, because a month name is language.
const UTC_HM = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
});
const UTC_MONTH_DAY = new Intl.DateTimeFormat(LOCALE, {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});
const utcDayKey = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * "10:02" (UTC), plus the UTC calendar date ("Sep 25") when the instant is not on
 * today's UTC day — the caller picks the copy with or without the date. NaN-safe:
 * an unparseable input comes back verbatim as `time` with no date (#357).
 */
export function utcWallClock(
  iso: string,
  now: number = Date.now(),
): { time: string; date: string | null } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { time: iso, date: null };
  const sameDay = utcDayKey(d) === utcDayKey(new Date(now));
  return { time: UTC_HM.format(d), date: sameDay ? null : UTC_MONTH_DAY.format(d) };
}

const DAY = new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium' });

/**
 * A CALENDAR DATE with no clock — "created", "joined", "last updated": the
 * rows where the hour is noise and the day is the fact.
 *
 * Six call sites printed one of these their own way: `MemberRow` built an
 * `Intl.DateTimeFormat(undefined, …)` (the raw BROWSER locale, so a French
 * browser read French dates under English copy), and `ConnectionTokenRow`,
 * `KbPageIndex`, `RepoSecretsView` and `LogSinkCard` called a bare
 * `toLocaleDateString()` — also the browser's, and in a SHORTER style, so the
 * same kind of column read `9/15/2026` on one screen and `Sep 15, 2026` on the
 * next.
 *
 * NaN-safe by passing the input through verbatim, this module's rule (#357),
 * rather than the unset marker `MemberRow` used: on an unparseable timestamp the
 * value that arrived is what the next debugger needs to see, and a dash hides
 * exactly that. Server-sent ISO never reaches the branch.
 */
export function day(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : DAY.format(d);
}

/**
 * "2h ago" style relative time. `now` is a parameter (like `since`'s and
 * `untilLabel`'s) for the callers that re-read a ticking clock signal — a row
 * rendering `ago` inside a per-minute `now()` effect stays honest in a drawer
 * left open, instead of freezing at the reading it mounted with.
 */
export function ago(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const mins = Math.round((then - now) / 60000);
  if (Math.abs(mins) < 60) return REL.format(mins, 'minute');
  const hrs = Math.round(mins / 60);
  if (Math.abs(hrs) < 24) return REL.format(hrs, 'hour');
  return REL.format(Math.round(hrs / 24), 'day');
}

/**
 * A USD amount, as the console prints money everywhere.
 *
 * Eight private `new Intl.NumberFormat('en-US', { style: 'currency', currency:
 * 'USD' })` constants said this. SEVEN collapsed here — `overview-tiles`,
 * `KeyCard`, `AdminOrgRow`, `BillingUsage`, `FleetStatus` (deleted since, with
 * the Fleet status band, #6559), `OverviewStats`, `AdminStatBand`. The eighth, `widget-format`'s cell formatter, KEEPS its own
 * and is counted as residual by `testing/formatter-seam.test.ts`: it formats an
 * opaque JSON cell under the widget grammar's declared `ColumnFormat`, not an
 * amount this app read off a billing body.
 *
 * `currency: 'USD'` is not a locale question: it is what the platform bills in
 * and what every `*_usd` field on the wire carries. The LOCALE decides the
 * grouping and the symbol's placement; the currency does not move.
 */
export function money(usd: number): string {
  return Number.isFinite(usd) ? MONEY.format(usd) : String(usd);
}

/**
 * The same amount when the server sent it as a DECIMAL STRING rather than a
 * number — `AdminAccountRow.spend_mtd_usd` and the admin rollup's spend band,
 * which carry Postgres `numeric` verbatim so no float ever rounds a bill.
 *
 * An unparseable value passes through as itself rather than becoming "NaN": on
 * an operator's console the raw wire value is the more useful of the two, and
 * it is the behaviour the two private copies of this (`AdminOrgRow`,
 * `AdminStatBand` — identical, line for line) already had.
 */
export function moneyFromDecimal(usd: string): string {
  const n = Number(usd);
  return Number.isFinite(n) ? MONEY.format(n) : usd;
}

const DECIMAL: Readonly<Record<1 | 2, Intl.NumberFormat>> = {
  1: new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 }),
  2: new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 2 }),
};

/**
 * A plain measured number (a GB amount, a load ratio) in the console's locale: "3.5" in
 * en, "3,5" in es. Moved here from `FleetAgentCard`'s private `en-US` GB formatter
 * (#6559), which the Fleet page's Needs attention copy now shares.
 */
export function decimal(n: number, digits: 1 | 2 = 1): string {
  return Number.isFinite(n) ? DECIMAL[digits].format(n) : String(n);
}

/**
 * A UTC calendar day (`'YYYY-MM-DD'`, the radar series' key, #6715) as "Oct 14" in the
 * console's locale. Pinned to UTC: the day IS a UTC date, and a western offset must not
 * roll it back to the 13th. Unparseable input passes through verbatim (#357).
 */
export function utcMonthDay(day: string): string {
  const d = new Date(`${day}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? day : UTC_MONTH_DAY.format(d);
}

const COUNT = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });

/** A whole count with the locale's grouping: "1,284" in en (Fleet Live's HUD, #6778). */
export function count(n: number): string {
  return Number.isFinite(n) ? COUNT.format(n) : String(n);
}

const COMPACT = new Intl.NumberFormat(LOCALE, { notation: 'compact', maximumFractionDigits: 1 });

/** A count shortened for an axis label: "102K", "1.6K", "14" (the radar chart's y axis, #6715). */
export function compact(n: number): string {
  return Number.isFinite(n) ? COMPACT.format(n) : String(n);
}

/**
 * THE ONE MINUTE CLOCK: a reactive wall-clock `now` (epoch ms) that ticks once a
 * minute, for a label relative to now that must not freeze while it is on
 * screen (a countdown, an idle age). Every subscriber shares ONE timer, armed by
 * the first and cleared with the last — a row that ran its own `setInterval`
 * was N timers ticking in step for an N-row list, and a timer nobody stopped is
 * a boot-lived resource (`docs/standards/long-running-resources.md`).
 *
 * Call it inside a component or root: the subscription is released by that
 * scope's cleanup. A new subscriber re-reads the wall clock rather than taking
 * the last tick, so a row mounted 59 s after it never opens a minute behind.
 */
export function useMinuteClock(): () => number {
  if (minuteSubscribers === 0) armMinuteTick();
  minuteSubscribers += 1;
  setMinuteNow(Date.now());
  onCleanup(() => {
    minuteSubscribers -= 1;
    if (minuteSubscribers > 0) return;
    if (minuteTimer !== undefined) clearTimeout(minuteTimer);
    minuteTimer = undefined;
  });
  return minuteNow;
}

const [minuteNow, setMinuteNow] = createSignal(Date.now());
let minuteSubscribers = 0;
let minuteTimer: ReturnType<typeof setTimeout> | undefined;

/** One pending tick at a time, re-armed by itself — so the cleanup has one handle to clear. */
function armMinuteTick(): void {
  minuteTimer = setTimeout(() => {
    setMinuteNow(Date.now());
    armMinuteTick();
  }, MINUTE_MS);
}

/**
 * Whole seconds until `atMs`, for a wait counted down in seconds (#6771: a move's 429, "Try
 * again in N s"): `max(1, ceil((atMs - nowMs) / 1000))`, the prototype's `secs` (`:1403`). A
 * null `atMs` is 1, as the server's `moveRetryAfterSeconds` answers a limit that frees on no
 * clock. Never 0: a wait still shown is at least a second. `untilLabel` rounds to minutes, so
 * this is the ONE seconds countdown.
 */
export function secondsUntil(atMs: number | null, nowMs: number): number {
  if (atMs === null || Number.isNaN(atMs)) return 1;
  return Math.max(1, Math.ceil((atMs - nowMs) / 1000));
}
